import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { api, errorMessage, post } from "@/lib/api";
import { formatTime, shortDay, typeLabel } from "@/utils/eventFormat";
import "./MyEvents.css";

/**
 * The signed-in membership's own events (events spec: student calendar,
 * parent shell, counselor shell). Read: GET /api/events/mine. Write: the
 * membership's own RSVP through POST /api/events/:id/rsvp (events.Respond),
 * only on rsvp / rsvp_and_attendance events, and never over a physical mark.
 */

export type MyEvent = {
  id: string;
  title: string;
  type: string;
  description: string | null;
  start_date: string | null;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  location: string | null;
  mode: string;
  require_rsvp: boolean;
  max_capacity: number | null;
  mine: string | null;
  capacity: { max: number | null; used: number; remaining: number | null; full: boolean };
};

const RSVP = [
  { value: "RSVP_YES", label: "Yes" },
  { value: "RSVP_NO", label: "No" },
  { value: "RSVP_MAYBE", label: "Maybe" },
];

const RSVP_TEXT: Record<string, string> = { RSVP_YES: "Yes", RSVP_NO: "No", RSVP_MAYBE: "Maybe" };
const PHYSICAL_TEXT: Record<string, string> = {
  PRESENT: "Present",
  ABSENT: "Absent",
  LATE: "Late",
  EXCUSED: "Excused",
};

/** Loads my events for [from, to] (YYYY-MM-DD). A failure leaves the list empty so the host page still works. */
export function useMyEvents(from: string, to: string) {
  const [events, setEvents] = useState<MyEvent[]>([]);
  const [canRespond, setCanRespond] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    const query = new URLSearchParams({ from, to });
    api<{ success: boolean; data: { events: MyEvent[]; canRespond: boolean } }>(`/api/events/mine?${query.toString()}`)
      .then((envelope) => {
        if (cancelled) return;
        setEvents(envelope.data?.events || []);
        setCanRespond(Boolean(envelope.data?.canRespond));
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  const replace = useCallback((next: MyEvent) => {
    setEvents((current) => current.map((event) => (event.id === next.id ? next : event)));
  }, []);

  return { events, canRespond, loaded, replace };
}

/** Each day (YYYY-MM-DD) an event covers, start through end. */
export function eventsByDay(events: MyEvent[]) {
  const map = new Map<string, MyEvent[]>();
  for (const event of events) {
    const start = event.start_date ? event.start_date.slice(0, 10) : "";
    if (!start) continue;
    const end = event.end_date && event.end_date.slice(0, 10) > start ? event.end_date.slice(0, 10) : start;
    const [y, m, d] = start.split("-").map(Number);
    const cursor = new Date(y, m - 1, d);
    for (let i = 0; i < 62; i += 1) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`;
      map.set(key, [...(map.get(key) || []), event]);
      if (key >= end) break;
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  return map;
}

function whenText(event: MyEvent) {
  const start = formatTime(event.start_time);
  const end = formatTime(event.end_time);
  const endDay = event.end_date && event.end_date !== event.start_date ? shortDay(event.end_date) : "";
  if (endDay) return [start, `until ${[endDay, end].filter(Boolean).join(" ")}`].filter(Boolean).join(" ");
  if (start && end) return `${start} – ${end}`;
  return start || "All day";
}

export function MyEventRow({
  event,
  canRespond,
  onChange,
  showDate = false,
}: {
  event: MyEvent;
  canRespond: boolean;
  onChange: (next: MyEvent) => void;
  showDate?: boolean;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const groupName = useId();
  const takesRsvp = event.mode !== "attendance";
  const marked = Boolean(event.mine && event.mine in PHYSICAL_TEXT);
  const full = Boolean(event.capacity?.full);

  async function respond(status: string) {
    if (status === event.mine) return;
    setSaving(true);
    setError("");
    const before = event;
    onChange({ ...event, mine: status });
    try {
      await post(`/api/events/${encodeURIComponent(event.id)}/rsvp`, { status });
      const wasYes = before.mine === "RSVP_YES";
      const isYes = status === "RSVP_YES";
      const used = (before.capacity?.used || 0) + (isYes && !wasYes ? 1 : 0) - (wasYes && !isYes ? 1 : 0);
      const max = before.capacity?.max ?? null;
      onChange({
        ...before,
        mine: status,
        capacity:
          max == null || before.mode === "attendance"
            ? before.capacity
            : { max, used, remaining: Math.max(0, max - used), full: used >= max },
      });
    } catch (err) {
      onChange(before);
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  let status = "";
  if (marked) status = `You were marked ${PHYSICAL_TEXT[event.mine as string]}.`;
  else if (!takesRsvp) status = "Attendance is taken at the event.";
  else if (event.mine) status = `You answered ${RSVP_TEXT[event.mine] || event.mine}.`;
  else if (!canRespond) status = "RSVP is not turned on for your account yet.";
  else if (event.require_rsvp) status = "Please answer.";
  else status = "You have not answered yet.";

  return (
    <article className="al-my-event">
      <div className="al-my-event__head">
        <strong className="al-my-event__title">{event.title}</strong>
        <span className="al-my-event__meta">
          {[showDate && event.start_date ? shortDay(event.start_date) : "", whenText(event), event.location, typeLabel(event.type)]
            .filter(Boolean)
            .join(" · ")}
        </span>
        {event.description ? <p className="al-my-event__desc">{event.description}</p> : null}
      </div>

      {takesRsvp && canRespond && !marked ? (
        <fieldset className="al-my-event__rsvp" disabled={saving}>
          <legend>RSVP{event.require_rsvp ? " (required)" : ""}</legend>
          <div>
            {RSVP.map((option) => {
              const blocked = option.value === "RSVP_YES" && full && event.mine !== "RSVP_YES";
              return (
                <label key={option.value} className={blocked ? "is-off" : ""}>
                  <input
                    type="radio"
                    name={groupName}
                    value={option.value}
                    checked={event.mine === option.value}
                    disabled={blocked}
                    onChange={() => void respond(option.value)}
                  />
                  {option.label}
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      <p className={`al-my-event__status${!event.mine && event.require_rsvp && takesRsvp && canRespond ? " is-due" : ""}`}>
        {saving ? "Saving…" : status}
        {takesRsvp && !marked && full && event.mine !== "RSVP_YES" ? " This event is full." : ""}
        {event.mode === "rsvp_and_attendance" && !marked ? " Attendance is also taken at the event." : ""}
      </p>
      {error ? (
        <p className="al-my-event__error" role="alert">
          {error}
        </p>
      ) : null}
    </article>
  );
}

function ymd(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * "Your upcoming events" for shells without a calendar (counselors land on My
 * Students). Next `days` days, soonest first. Renders nothing when there are none.
 */
export function MyUpcomingEvents({ days = 60 }: { days?: number }) {
  const range = useMemo(() => {
    const start = new Date();
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + days);
    return { from: ymd(start), to: ymd(end) };
  }, [days]);
  const mine = useMyEvents(range.from, range.to);
  const sorted = useMemo(
    () =>
      [...mine.events].sort((a, b) =>
        `${a.start_date || ""} ${a.start_time || ""}`.localeCompare(`${b.start_date || ""} ${b.start_time || ""}`),
      ),
    [mine.events],
  );
  if (!mine.loaded || sorted.length === 0) return null;
  const needAnswer = sorted.filter(
    (event) => mine.canRespond && event.mode !== "attendance" && !event.mine && event.require_rsvp,
  ).length;
  return (
    <section className="al-my-events" aria-labelledby="al-my-events-title">
      <div className="al-my-events__head">
        <h2 id="al-my-events-title">Your upcoming events</h2>
        <span className="al-my-event__meta">
          {needAnswer ? `${needAnswer} need${needAnswer === 1 ? "s" : ""} your RSVP` : `Next ${days} days`}
        </span>
      </div>
      {sorted.map((event) => (
        <MyEventRow key={event.id} event={event} canRespond={mine.canRespond} onChange={mine.replace} showDate />
      ))}
    </section>
  );
}
